import { validate as isValidUUID } from 'uuid';

import { DEFAULT_ROUTE } from '#client/build-env';
import * as ROUTES from '#client/constants/routes';

// Routes that should be excluded from back navigation
const EXCLUDED_BACK_ROUTES = ['guide/thread', 'guide/company', '/f/'];

// Default fallback route when business entity is unavailable
const DEFAULT_DASHBOARD_FALLBACK_ROUTE = '/dashboard';

const DEFAULT_FALLBACK_ROUTE = '/velo';
/**
 * Allowed redirect path patterns
 * These are the only paths that can be used as redirect destinations
 * Derived from routes.js constants
 */
const ALLOWED_REDIRECT_PATTERNS = [
  // Auth routes
  ROUTES.callback,
  ROUTES.login,
  ROUTES.logout,

  // Main routes
  ROUTES.landing,
  ROUTES.test,
  ROUTES.threadCreator,
  ROUTES.thread,

  // Visitor routes
  ROUTES.businessIdea,
  ROUTES.visitorThreadCreator,
  ROUTES.visitorThread,

  // Chat routes
  ROUTES.chatEntry,

  // Default route
  DEFAULT_ROUTE,
  DEFAULT_FALLBACK_ROUTE,
  DEFAULT_DASHBOARD_FALLBACK_ROUTE,

  // Additional common patterns
  '/dashboard/:businessEntityUuid'
].filter(Boolean); // Filter out undefined routes

/**
 * Check if a route should be excluded from back navigation
 * @param {string} path - The path to check
 * @returns {boolean} True if the route should be excluded
 */
export const isExcludedRoute = (path) => {
  if (!path) {
    return false;
  }

  return EXCLUDED_BACK_ROUTES.some((excludedRoute) => {
    return path.toLowerCase().includes(excludedRoute);
  });
};

/**
 * Check if a URL is an internal route (same origin)
 * @param {string} url - The URL to check
 * @returns {boolean} True if the URL is internal
 */
export const isInternalRoute = (url) => {
  if (!url) return false;
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Validate business entity state and return appropriate action
 * Supports multiple user modes:
 * - Anonymous/Starter: No businessEntityUuid required
 * - Business: Validates businessEntityUuid if provided
 *
 * @param {object} businessEntity - Business entity object from hook
 * @param {string|null|undefined} businessEntity.businessEntityUuid - Business entity UUID (optional for anon/starter modes)
 * @param {boolean} businessEntity.loading - Loading state
 * @param {Error|null} businessEntity.error - Error state
 * @returns {object} Validation result with status and route
 * @returns {boolean} return.isValid - Whether the state is valid for navigation
 * @returns {string} return.reason - Reason for validation result
 * @returns {string} return.route - Fallback route (used only if navigation context fails)
 * @returns {string|null} return.message - Human-readable message
 * @returns {Error} [return.error] - Error object if applicable
 */
export const validateBusinessEntityState = (businessEntity) => {
  const { businessEntityUuid, loading, error } = businessEntity;

  // Handle loading state (always matters regardless of mode)
  if (loading) {
    return {
      route: DEFAULT_FALLBACK_ROUTE
    };
  }

  // Handle error state (always matters regardless of mode)
  if (error) {
    return {
      route: DEFAULT_FALLBACK_ROUTE
    };
  }

  // If no businessEntityUuid (anonymous or starter mode)
  // This is valid - user is in a mode that doesn't require business entity
  // Use chat entry route as fallback for non-business users
  if (!businessEntityUuid) {
    return {
      route: DEFAULT_FALLBACK_ROUTE
    };
  }

  // If businessEntityUuid exists (business mode), validate it
  if (!isValidUUID(businessEntityUuid)) {
    console.warn('[Navigation] Invalid business entity UUID:', businessEntityUuid);
    return {
      route: DEFAULT_FALLBACK_ROUTE
    };
  }

  // Valid business entity UUID
  return {
    route: `/dashboard/${businessEntityUuid}`
  };
};

/**
 * Determine the best navigation strategy based on context and current state
 * Strategy order:
 * 1. For excluded routes (like chat threads): try history → referrer → dashboard
 * 2. For other routes: try history → referrer → dashboard
 *
 * @param {object} context - Navigation context
 * @param {object} location - Current location object
 * @param {string} dashboardRoute - Valid dashboard route (from validateBusinessEntityState)
 * @returns {object} Navigation strategy with type and target
 */
export const determineNavigationStrategy = (context, location, dashboardRoute) => {
  const currentPath = location.pathname;
  // const hasHistoryAvailable = window.history.length > (context?.historyLength || 1);

  // Strategy 1: For excluded routes (like chat threads)
  if (isExcludedRoute(currentPath)) {
    // // 1a. Try browser back if we have history
    // if (hasHistoryAvailable) {
    //   return {
    //     type: 'back',
    //     target: -1,
    //     reason: 'excluded_route_with_history'
    //   };
    // }

    // 1b. Try referrer if valid and NOT an excluded route
    if (context?.referrer && isInternalRoute(context.referrer)) {
      const referrerPath = context.referrer;

      if (!isExcludedRoute(referrerPath)) {
        return {
          type: 'referrer',
          target: referrerPath
        };
      }
    }

    // 1c. Fallback to dashboard for excluded routes
    return {
      type: 'dashboard',
      target: dashboardRoute
    };
  }

  // Strategy 2: For non-excluded routes
  // 2a. Try browser back if we have history
  // if (hasHistoryAvailable) {
  //   return {
  //     type: 'back',
  //     target: -1,
  //     reason: 'history_available'
  //   };
  // }

  // 2b. Navigate to referrer if valid and not excluded
  if (context?.referrer && isInternalRoute(context.referrer)) {
    const referrerPath = context.referrer;

    if (!isExcludedRoute(referrerPath)) {
      return {
        type: 'referrer',
        target: referrerPath
      };
    }
  }

  // 2c. Default fallback to dashboard
  return {
    type: 'dashboard',
    target: dashboardRoute
  };
};

/**
 * Safely decode a base64-encoded URL
 * @param {string} encodedUrl - Potentially base64-encoded URL
 * @returns {string|null} Decoded URL or null if invalid
 */
export const decodeRedirectUrl = (encodedUrl) => {
  if (!encodedUrl || typeof encodedUrl !== 'string') {
    return null;
  }

  try {
    // Check if it looks like base64 (alphanumeric + / + = padding)
    if (/^[A-Za-z0-9+/]+=*$/.test(encodedUrl)) {
      const decoded = atob(encodedUrl);
      // Only return decoded value if it looks like a path (starts with /)
      if (decoded.startsWith('/')) {
        return decoded;
      }
    }
  } catch (error) {
    // Not valid base64, return null
    return null;
  }

  // If not base64, check if it's already a valid path
  if (encodedUrl.startsWith('/')) {
    return encodedUrl;
  }

  return null;
};

/**
 * Check if a path matches any allowed redirect pattern
 * Handles dynamic route segments like :businessEntityUuid, :threadUuid
 * @param {string} path - The path to validate
 * @returns {boolean} True if path matches an allowed pattern
 */
export const isAllowedRedirectPath = (path) => {
  if (!path || typeof path !== 'string') {
    return false;
  }

  // Prevent open redirect: Path must start with / and NOT start with //
  if (!path.startsWith('/') || path.startsWith('//')) {
    return false;
  }

  // Prevent protocol-relative URLs and other sneaky redirects
  if (path.includes(':') || path.includes('\\')) {
    return false;
  }

  // Normalize path (remove trailing slash for comparison)
  const normalizedPath = path.replace(/\/$/, '');

  // Check exact matches first
  for (const pattern of ALLOWED_REDIRECT_PATTERNS) {
    if (!pattern) continue;

    const normalizedPattern = pattern.replace(/\/$/, '');

    // Exact match
    if (normalizedPath === normalizedPattern) {
      return true;
    }

    // Check if pattern has dynamic segments (contains :)
    if (normalizedPattern.includes(':')) {
      // Convert route pattern to regex
      // Example: /guide/thread/:threadUuid -> /guide/thread/[^/]+
      const regexPattern = normalizedPattern
        .replace(/:[^/]+/g, '[^/]+') // Replace :param with [^/]+ (one or more non-slash chars)
        .replace(/\//g, '\\/'); // Escape slashes for regex

      const regex = new RegExp(`^${regexPattern}$`);

      if (regex.test(normalizedPath)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Validate and sanitize a redirect URL for use in authentication callbacks
 * Prevents open redirects by:
 * 1. Safely decoding base64-encoded URLs
 * 2. Validating against whitelist of allowed paths
 * 3. Rejecting external URLs and protocol-relative URLs
 *
 * @param {string} redirectUrl - URL or base64-encoded URL to validate
 * @param {string} fallbackUrl - Fallback URL if validation fails (default: DEFAULT_ROUTE)
 * @returns {string} Validated redirect URL or fallback
 *
 * @example
 * // Direct path
 * validateAndSanitizeRedirectUrl('/guide/chat') // -> '/guide/chat'
 *
 * @example
 * // Base64 encoded path
 * validateAndSanitizeRedirectUrl('L2d1aWRlL2NoYXQ=') // -> '/guide/chat'
 *
 * @example
 * // Invalid path
 * validateAndSanitizeRedirectUrl('//evil.com') // -> DEFAULT_ROUTE
 *
 * @example
 * // Not in whitelist
 * validateAndSanitizeRedirectUrl('/admin/users') // -> DEFAULT_ROUTE
 */
export const validateAndSanitizeRedirectUrl = (redirectUrl, fallbackUrl = DEFAULT_ROUTE) => {
  // Step 1: Decode if base64
  const decodedUrl = decodeRedirectUrl(redirectUrl);

  // Step 2: Validate against whitelist
  if (decodedUrl && isAllowedRedirectPath(decodedUrl)) {
    return decodedUrl;
  }

  // Step 3: Return fallback if validation failed
  return fallbackUrl || DEFAULT_FALLBACK_ROUTE;
};
